// period.ts — TR bordro period-boundary derivation (W3 period integrity).
//
// currentIstanbulMonthBounds() replaces the raw `new Date()` local-clock
// derivations admin-payroll-content.tsx / exit-payroll-content.tsx used to
// compute "this month" for a new regular/exit pay run. A caller's BROWSER-
// local calendar month can differ from Turkey's near a month boundary (a
// caller in a timezone behind or ahead of Europe/Istanbul); platform-graph's
// CreateRunSchema now HARD-REJECTS a run_kind=regular run whose
// period_start/period_end aren't the exact first/last day of a calendar
// month (Y-M-D string math, spec 69 period integrity) — a client deriving
// the wrong month off its local clock would 400 at submit.
//
// Deliberately NOT entity_tenant.default_time_zone: that field is the UI
// DISPLAY timezone preference; a TR bordro period boundary is scoped by
// Turkish payroll law (SGK / gelir vergisi periods run on Turkey's calendar,
// not a tenant's chosen display timezone), so this is hardcoded to
// Europe/Istanbul regardless of the tenant's display-timezone setting.
//
// NO new dependency: date-fns-tz is not in this repo (CLAUDE.md tech-stack
// pin, ADR-013). Intl.DateTimeFormat('en-CA', { timeZone }).formatToParts is
// the zero-dependency way to read a wall-clock (year, month) pair in a given
// IANA zone; Date.UTC(...) then does the last-day-of-month arithmetic against
// those already-resolved plain numbers — pure calendar math, no further
// timezone conversion.

export interface MonthBounds {
  /** "Temmuz 2026" — the stored period_label; see {@link monthYearLabel} for why it is Turkish. */
  readonly periodLabel: string;
  /** ISO date (YYYY-MM-DD), the 1st of the month. */
  readonly periodStart: string;
  /** ISO date (YYYY-MM-DD), the month's last day. */
  readonly periodEnd: string;
}

/** The (year, month) Turkey's wall clock reads for `now`, regardless of the caller's local timezone. month is 1-indexed (1 = January). */
function istanbulYearMonth(now: Date): {
  readonly year: number;
  readonly month: number;
} {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(now);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  return { year, month };
}

/**
 * The current calendar month's bounds on Turkey's wall clock — the 1st
 * through the last day. `now` is injectable for tests; defaults to the real
 * clock.
 */
export function currentIstanbulMonthBounds(
  now: Date = new Date(),
): MonthBounds {
  const { year, month } = istanbulYearMonth(now);
  return calendarMonthBounds(year, month);
}

/** A native month input is a calendar value, never a browser-local instant. */
export function selectedMonthBounds(value: string): MonthBounds | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (match === null || Number(match[1]) < 1000) return null;
  return calendarMonthBounds(Number(match[1]), Number(match[2]));
}

function calendarMonthBounds(year: number, month: number): MonthBounds {
  const y = String(year);
  const m = String(month).padStart(2, "0");
  // Date.UTC's month parameter is 0-indexed, so passing the 1-indexed
  // `month` value directly addresses the NEXT month; day=0 rolls back one
  // day, landing on THIS month's last day (the same "next month, day 0"
  // trick the prior raw-Date code used, just against UTC-resolved plain
  // numbers instead of the browser's local clock).
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    // The STORED label is Turkish regardless of the creator's UI language:
    // it is one shared string that every viewer, approval summary and audit
    // row reads back, and the seeded periods ("Temmuz 2026") already use it.
    // Storing "08/2026" here is what made one list mix two formats.
    periodLabel: monthYearLabel("tr-TR", year, month),
    periodStart: `${y}-${m}-01`,
    periodEnd: `${y}-${m}-${String(lastDay).padStart(2, "0")}`,
  };
}

/** "Temmuz 2026" / "July 2026" — `month` is 1-indexed. UTC-pinned so the viewer's offset cannot move it. */
function monthYearLabel(locale: string, year: number, month: number): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

interface RunPeriod {
  readonly run_kind: string;
  readonly period_label: string;
  readonly period_start: string;
  readonly period_end: string;
}

/**
 * How a run's period reads on screen.
 *
 * `period_label` is free text the creating client chose, so the same list held
 * "Temmuz 2026" (seeded) beside "08/2026" (created in the UI). A regular run
 * that spans exactly one calendar month is therefore titled from its dates, in
 * the VIEWER's language; every other run (exit, correction, supplemental, an
 * off-calendar range) keeps its label, because there the label carries meaning
 * the dates do not ("Çıkış Ağustos 2026").
 */
export function formatRunPeriod(locale: string, run: RunPeriod): string {
  if (run.run_kind !== "regular") return run.period_label;
  const start = /^(\d{4})-(\d{2})-01$/.exec(run.period_start);
  if (start === null) return run.period_label;
  const year = Number(start[1]);
  const month = Number(start[2]);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const monthEnd = `${start[1]}-${start[2]}-${String(lastDay).padStart(2, "0")}`;
  return run.period_end === monthEnd
    ? monthYearLabel(locale, year, month)
    : run.period_label;
}
